<template>
	<Col class="page-wrapper height-100 width-100">
		<Col class="page-container overflow-x-hidden height-100 width-100 overflow-y-auto">
			<Row class="flex-align-center flex-justify-space-between mb-2">
				<Col>
					<Row class="page-title">Analytics</Row>
					<h3 class="reg text-[#646E87]">Learn how your features are used</h3>
				</Col>
				<AnalyticsDataFilter
					:dateRangeFilter.sync="dateRangeFilter"
					:hasAccessToAllSpaces="hasAccessToAllSpaces"
					:includeAllSpacesForAdmin="includeAllSpacesForAdmin"
					@include-all-spaces-for-admin-changed="(v) => (includeAllSpacesForAdmin = v)"
				/>
			</Row>
			<router-link v-if="!isDashboardMode" class="router" :to="toDashboard">
				<Row class="flex-align-center">
					<AnemicButton class="back-button" width="25" height="25" fab x-small>
						<AppIcon name="back" />
					</AnemicButton>
					<h3 class="reg ml-3 pointer-events-none">Back</h3>
				</Row>
			</router-link>
			<h2 v-if="showSubPageTitle" class="heavy my-5">{{ subPageTitle }}</h2>
			<v-slide-x-transition mode="out-in">
				<AppLoader class="width-100 height-100 mt-9" v-if="isLoading" />
				<router-view :includeAllSpacesForAdmin="includeAllSpacesForAdmin" v-else class="width-100 height-100" />
			</v-slide-x-transition>
		</Col>
	</Col>
</template>
<script>
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import AnalyticsDataFilter from '@/components/Analytics/AnalyticsDataFilter.vue';
import AnalyticsService from '@/components/Analytics/analytics-service';
import AnalyticsMixin from '@/components/Analytics/mixins/AnalyticsMixin';
import { ANALYTICS_DASHBOARD_ROUTE_NAME } from '@/router/router.const';
import { hasAccountPermission } from '@/store/modules/account/getters';
import {
	clearAnalyticsState,
	getAnalyticsRunsByDay,
	getAnalyticsRunsByUser,
	getAnalyticsLatestErrors,
	getAnalyticsRunsByFeature,
} from '@/store/modules/analytics/actions';
import { isAccountOwner } from '@/store/modules/auth/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import CreateAndManageService from '@/views/AppLayout/ManageAndCreate/create-and-manage-service';
export default {
	name: 'Analytics',
	mixins: [AnalyticsMixin],
	components: { AnalyticsDataFilter },
	computed: {
		...mapGettersShorthand({ isAccountOwner, hasAccountPermission }),
		toDashboard() {
			return { name: this.routeNamePrefix + ANALYTICS_DASHBOARD_ROUTE_NAME };
		},
		isDashboardMode() {
			return this.$route.name === this.toDashboard.name;
		},
		routeNamePrefix() {
			return CreateAndManageService.getPlacementRoutePrefix(this.$route);
		},
		isAnalyticsDashboard() {
			return AnalyticsService.isAnalyticsDashboard(this.$route);
		},
		showSubPageTitle() {
			return !this.isAnalyticsDashboard && this.subPageTitle;
		},
		subPageTitle() {
			return AnalyticsService.getSubPageTitle(this.$route);
		},
		includeAllSpacesForAdminFromQuery() {
			return this.$route.query?.includeAllSpacesForAdmin;
		},
		hasAccessToAllSpaces() {
			return this.hasAccountPermission(AccountPermissions.ViewOtherPrivateWorkflows);
		},
		includeAllSpacesForAdmin: {
			get() {
				if (this.hasAccessToAllSpaces) {
					return ['true', true].includes(this.includeAllSpacesForAdminFromQuery);
				} else {
					return false;
				}
			},
			set(value) {
				if (this.hasAccessToAllSpaces) {
					const query = { ...this.$route.query };
					if (value) {
						query.includeAllSpacesForAdmin = value;
					} else {
						delete query.includeAllSpacesForAdmin;
					}
					if (!isEqual(query, this.$route.query)) {
						this.$router.push({ ...this.$route, query });
					}
				}
			},
		},
	},
	methods: {
		...mapActionsShorthand({
			clearAnalyticsState,
			getAnalyticsRunsByDay,
			getAnalyticsRunsByUser,
			getAnalyticsLatestErrors,
			getAnalyticsRunsByFeature,
		}),
		getDateRangeFiltersFormatted() {
			const now = moment();
			const dateFormat = 'YYYY-MM-DD';
			const latestFilter = this.dateRangeFilter[1];
			const earliestFilter = this.dateRangeFilter[0];

			const earliestFilterWithOneMoreDay = moment(earliestFilter, dateFormat).add(1, 'days');

			const latestDate = latestFilter
				? moment(latestFilter, dateFormat)
				: earliestFilter
				? earliestFilterWithOneMoreDay.isSameOrAfter(now)
					? now
					: earliestFilterWithOneMoreDay
				: now;

			const earliestDate = earliestFilter ? moment(earliestFilter, dateFormat) : now.subtract(29, 'days');
			const nowISO = now.toISOString();
			const dateRangeGTE = earliestDate.toISOString();
			const isLatestDateToday = latestDate.isSame(now, 'day');
			const dateRangeLTE = isLatestDateToday ? nowISO : latestDate.toISOString();
			return { dateRangeGTE, dateRangeLTE };
		},
		async fetchAllData() {
			this.isLoading = true;
			this.clearAnalyticsState();
			const limit = this.maxNumberOfItemsInPreviewCard;
			const includeAllSpaces = this.includeAllSpacesForAdmin;
			const { dateRangeGTE, dateRangeLTE } = this.getDateRangeFiltersFormatted();
			const filters = {
				dateRangeGTE,
				dateRangeLTE,
				includeAllSpaces,
				spaceId: this.activeSpaceId,
			};

			await Promise.all([
				this.getAnalyticsRunsByDay(filters),
				this.getAnalyticsLatestErrors(filters),
				this.getAnalyticsRunsByUser({ limit, ...filters }),
				this.getAnalyticsRunsByFeature({ limit, ...filters }),
			]);
			this.isLoading = false;
		},
		initializeIncludeAllSpacesFlag() {
			this.includeAllSpacesForAdmin = this.hasAccessToAllSpaces && this.includeAllSpacesForAdminFromQuery;
		},
	},
	async mounted() {
		this.initializeIncludeAllSpacesFlag();
		await this.fetchAllData();
	},
	beforeDestroy() {
		this.clearAnalyticsState();
	},
	data() {
		return {
			isLoading: false,
			dateRangeFilter: [],
		};
	},
	watch: {
		includeAllSpacesForAdmin() {
			this.fetchAllData();
		},
		dateRangeFilter() {
			if (this.dateRangeFilter.length === 2) {
				this.fetchAllData();
			}
		},
	},
};
</script>
<style scoped lang="scss">
.page-wrapper {
	background: #f9fafc;
}
.page-container {
	margin: 0px 0px 72px 0px;
	padding: 16px 72px 32px 72px;
}
.page-title {
	font-size: 18px;
	font-weight: 600;
	color: var(--primary-font-color);
}
.back-button {
	background: #f9fafc !important;
	border: 1px solid #e0e0e0 !important;
}
.router {
	width: 25px;
	text-decoration: none;
}
</style>
